import React, { useId, useMemo, useRef, useState } from 'react';
import {
  formatDescription,
  renderDescriptionMarkdown,
  type DescriptionFormat,
} from './description-markdown';

const formats: { format: DescriptionFormat; label: string; glyph: string }[] = [
  { format: 'bold', label: 'Bold', glyph: 'B' },
  { format: 'italic', label: 'Italic', glyph: 'I' },
  { format: 'heading', label: 'Heading', glyph: 'H2' },
  { format: 'list', label: 'Bullet list', glyph: 'List' },
  { format: 'quote', label: 'Blockquote', glyph: 'Quote' },
  { format: 'link', label: 'Insert link', glyph: 'Link' },
];
export function DescriptionEditor({
  value,
  change,
}: {
  value: string | null;
  change(value: string | null): void;
}) {
  const id = useId();
  const editor = useRef<HTMLTextAreaElement>(null);
  const [preview, setPreview] = useState(false);
  const source = value ?? '';
  const html = useMemo(() => renderDescriptionMarkdown(source), [source]);
  const apply = (format: DescriptionFormat) => {
    const field = editor.current;
    if (!field || field.matches(':disabled')) return;
    const next = formatDescription(source, field.selectionStart, field.selectionEnd, format);
    if (next.value.length > 12000) return;
    change(next.value);
    requestAnimationFrame(() => {
      field.focus();
      field.setSelectionRange(next.selectionStart, next.selectionEnd);
    });
  };
  return (
    <section className="sm:col-span-2" aria-labelledby={`${id}-label`}>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <label id={`${id}-label`} htmlFor={id}>
          Description
        </label>
        <div className="flex gap-1" role="group" aria-label="Description editor mode">
          {['Write', 'Preview'].map((name) => (
            <button
              key={name}
              type="button"
              aria-pressed={preview === (name === 'Preview')}
              className="min-h-11 rounded-lg px-3 text-sm font-semibold aria-pressed:bg-thread-soft aria-pressed:text-thread focus-visible:outline-2 focus-visible:outline-thread"
              onClick={() => setPreview(name === 'Preview')}
            >
              {name}
            </button>
          ))}
        </div>
      </div>
      <div className="overflow-hidden rounded-xl border border-line/60 bg-panel-raised">
        {!preview && (
          <>
            <div
              role="group"
              aria-label="Description formatting"
              className="flex flex-wrap gap-1 border-b border-line/60 p-1"
            >
              {formats.map(({ format, label, glyph }) => (
                <button
                  key={format}
                  type="button"
                  title={label}
                  aria-label={label}
                  className={`min-h-11 min-w-11 rounded-lg px-3 text-sm hover:bg-thread/10 focus-visible:outline-2 focus-visible:outline-thread ${format === 'bold' ? 'font-bold' : format === 'italic' ? 'italic' : 'font-medium'}`}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => apply(format)}
                >
                  {glyph}
                </button>
              ))}
            </div>
            <textarea
              id={id}
              ref={editor}
              value={source}
              rows={8}
              maxLength={12000}
              aria-describedby={`${id}-hint`}
              className="block w-full resize-y bg-transparent p-4 text-ink outline-thread"
              onChange={(event) => change(event.target.value || null)}
              onKeyDown={(event) => {
                if (
                  (event.metaKey || event.ctrlKey) &&
                  ['b', 'i'].includes(event.key.toLowerCase())
                ) {
                  event.preventDefault();
                  apply(event.key.toLowerCase() === 'b' ? 'bold' : 'italic');
                }
              }}
            />
          </>
        )}
        {preview && (
          <div role="region" aria-label="Description preview" className="min-h-48 p-4">
            {source.trim() ? (
              <div className="description-markdown" dangerouslySetInnerHTML={{ __html: html }} />
            ) : (
              <p className="text-ink-muted">Nothing to preview yet. Write a description first.</p>
            )}
          </div>
        )}
      </div>
      <div className="mt-2 flex justify-between gap-3 text-xs text-ink-muted">
        <p id={`${id}-hint`}>Markdown supported. Images and HTML are not rendered.</p>
        <span>{source.length.toLocaleString('en')} / 12,000</span>
      </div>
    </section>
  );
}
